// src/DetalhesReservaModal.js

import React, { useState, useEffect, useRef } from 'react';
import { db } from './firebaseConfig';
import { doc, getDoc, updateDoc, collection, query, addDoc, serverTimestamp, deleteDoc, onSnapshot, orderBy } from 'firebase/firestore';
import { toast } from 'react-toastify';
import useReservasStore from './store/reservasStore';

// Modais
import PagamentoModal from './PagamentoModal';
import DespesaExtraModal from './DespesaExtraModal';
import FichaCheckinModal from './FichaCheckinModal';
import VouchersModal from './VouchersModal';
import CancelamentoModal from './CancelamentoModal';
import MudarChaleModal from './MudarChaleModal';

// Componentes MUI
import {
  Dialog, DialogActions, DialogContent, DialogTitle, Button, IconButton, Box, Grid,
  Typography, Paper, Divider, CircularProgress, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tooltip,
} from '@mui/material';

// Ícones MUI
import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import EditIcon from '@mui/icons-material/Edit';
import PaymentIcon from '@mui/icons-material/Payment';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import ReceiptIcon from '@mui/icons-material/Receipt';
import LocalActivityIcon from '@mui/icons-material/LocalActivity';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import EmailIcon from '@mui/icons-material/Email';
import DoNotDisturbOnIcon from '@mui/icons-material/DoNotDisturbOn';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';

// PDF
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const getHojeFormatado = () => new Date().toISOString().slice(0, 10);
const formatarData = (dataStr) => { if (!dataStr) return 'N/A'; const p = dataStr.split('-'); return p.length !== 3 ? dataStr : `${p[2]}/${p[1]}/${p[0]}`; };
const formatarMoeda = (valor) => { if (typeof valor !== 'number') { valor = 0; } return valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }); };
const ConfirmacaoToast = ({ closeToast, onConfirm, message }) => ( <div><p>{message}</p><Button onClick={() => { onConfirm(); closeToast(); }} variant="contained" color="error" size="small" sx={{ mr: 1 }}>Sim</Button><Button onClick={closeToast} variant="outlined" size="small">Cancelar</Button></div> );


const DetalhesReservaModal = ({ reserva: reservaProp, onClose, onExcluir, onEditar }) => {
  const { fetchInitialData, configuracoes, chales } = useReservasStore();
  
  const [reservaLocal, setReservaLocal] = useState(null);
  const [detalhesHospede, setDetalhesHospede] = useState(null);
  const [despesasExtras, setDespesasExtras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPagamentoModalOpen, setIsPagamentoModalOpen] = useState(false);
  const [isDespesaModalOpen, setIsDespesaModalOpen] = useState(false);
  const [isFichaModalOpen, setIsFichaModalOpen] = useState(false);
  const [isVouchersModalOpen, setIsVouchersModalOpen] = useState(false);
  const [isCancelamentoModalOpen, setIsCancelamentoModalOpen] = useState(false);
  const [isMudarChaleModalOpen, setIsMudarChaleModalOpen] = useState(false);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [isCheckingIn, setIsCheckingIn] = useState(false);
  const resumoRef = useRef(null);

  useEffect(() => {
    if (!reservaProp?.id) {
        setLoading(false);
        return;
    }
    setLoading(true);

    const reservaRef = doc(db, 'reservas', reservaProp.id);
    const unsubReserva = onSnapshot(reservaRef, (docSnap) => {
        if (docSnap.exists()) {
            setReservaLocal({ id: docSnap.id, ...docSnap.data() });
        } else {
            toast.error("Esta reserva não existe mais.");
            onClose();
        }
    });

    const despesasRef = collection(db, 'reservas', reservaProp.id, 'consumos');
    const qDespesas = query(despesasRef, orderBy('dataLancamento', 'desc'));
    const unsubDespesas = onSnapshot(qDespesas, (querySnapshot) => {
        setDespesasExtras(querySnapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    return () => {
        unsubReserva();
        unsubDespesas();
    };
  }, [reservaProp?.id, onClose]);

  useEffect(() => {
    const fetchHospede = async () => {
        if (reservaLocal?.hospedeId) {
            const hospedeRef = doc(db, 'hospedes', reservaLocal.hospedeId);
            const hospedeSnap = await getDoc(hospedeRef);
            if (hospedeSnap.exists()) {
                setDetalhesHospede({id: hospedeSnap.id, ...hospedeSnap.data()});
            } else {
                toast.warn("Hóspede não encontrado.");
                setDetalhesHospede({ nome: 'Hóspede não encontrado' });
            }
        }
        if (reservaLocal) setLoading(false);
    };
    fetchHospede();
  }, [reservaLocal]);


  const handleSavePagamento = async (dadosPagamento) => {
    const reservaRef = doc(db, 'reservas', reservaLocal.id);
    try {
      await updateDoc(reservaRef, dadosPagamento);
      toast.success("Pagamento atualizado com sucesso!");
      await fetchInitialData();
    } catch (error) { toast.error("Erro ao salvar pagamento."); }
  };

  const handleSaveDespesa = async (novaDespesa) => {
    try {
      await addDoc(collection(db, 'reservas', reservaLocal.id, 'consumos'), { ...novaDespesa, dataLancamento: serverTimestamp() });
      toast.success("Despesa adicionada!");
    } catch (error) {
      toast.error("Erro ao adicionar despesa.");
    }
  };

  const handleExcluirDespesa = (despesaId) => {
    const doDelete = async () => {
      await deleteDoc(doc(db, 'reservas', reservaLocal.id, 'consumos', despesaId));
      toast.success("Despesa excluída!");
    };
    toast.info(<ConfirmacaoToast message="Excluir esta despesa?" onConfirm={doDelete} />, { autoClose: false });
  };

  const handleExcluirReserva = () => {
    const doDelete = () => {
        onExcluir(reservaLocal.id);
        onClose();
    };
    toast.info(<ConfirmacaoToast message="Tem certeza que deseja EXCLUIR PERMANENTEMENTE este registro?" onConfirm={doDelete} />, { autoClose: false });
  };

  const handleConfirmarCancelamento = async (calculo) => {
    const reservaRef = doc(db, 'reservas', reservaLocal.id);
    try {
        await updateDoc(reservaRef, {
            statusReserva: 'Cancelada',
            cancelamentoInfo: {
                data: serverTimestamp(),
                motivo: 'Cancelado pelo Hóspede',
                ...calculo
            }
        });
        toast.success("Reserva cancelada com sucesso!");
        setIsCancelamentoModalOpen(false);
        await fetchInitialData();
    } catch (error) {
        toast.error("Erro ao registrar o cancelamento.");
    }
  };

  const handleConfirmarMudanca = async (novoChale, motivo) => {
    const reservaRef = doc(db, 'reservas', reservaLocal.id);
    const despesasRef = collection(db, 'reservas', reservaLocal.id, 'consumos');
    try {
        await updateDoc(reservaRef, {
            chaletId: novoChale.id,
            chaleNome: novoChale.nome,
            chaleNumero: novoChale.numero,
        });
        await addDoc(despesasRef, {
            descricao: `MUDANÇA DE CHALÉ: Do ${reservaLocal.chaleNome} para ${novoChale.nome}. Motivo: ${motivo}`,
            valor: 0,
            dataLancamento: serverTimestamp(),
        });
        toast.success("Mudança de chalé realizada com sucesso!");
        setIsMudarChaleModalOpen(false);
        await fetchInitialData();
    } catch (error) {
        toast.error("Erro ao realizar a mudança de chalé.");
    }
  };

  const handleCheckIn = async () => {
    setIsCheckingIn(true);
    try {
        await updateDoc(doc(db, 'reservas', reservaLocal.id), { statusReserva: 'Hospedado' });
        toast.success(`Check-in de ${detalhesHospede?.nome || ''} realizado!`);
        await fetchInitialData();
    } catch(e){
        toast.error("Erro ao fazer check-in.");
    } finally {
        setIsCheckingIn(false);
    }
  };

  const handleCheckout = async () => {
    const doCheckout = async () => {
        const reservaRef = doc(db, 'reservas', reservaLocal.id);
        await updateDoc(reservaRef, { statusReserva: 'Finalizada' });
        const chaleRef = doc(db, 'chales', reservaLocal.chaletId);
        await updateDoc(chaleRef, { status: 'Em Limpeza' });
        toast.success(`Check-out realizado! O chalé ${reservaLocal.chaleNome} está em limpeza.`);
        await fetchInitialData();
        onClose();
    };
    toast.info(<ConfirmacaoToast message="Confirmar o check-out?" onConfirm={doCheckout} />, { autoClose: false });
  };
  
  const handleEnviarEmail = () => {
    if (!reservaLocal || !detalhesHospede) return;
    
    const acompanhantesTexto = (reservaLocal.acompanhantes || [])
        .filter(a => a.nome)
        .map(a => `- ${a.nome}`)
        .join('\n');

    const dias = configuracoes?.diasAntecedenciaCancelamento || 15;
    const percentual = configuracoes?.percentualMultaAntecedencia || 20;

    const politicaCancelamento = `POLÍTICA DE CANCELAMENTO DE RESERVA
O cancelamento da reserva feito até ${dias} dias do check-in será cobrado ${percentual}% ao hóspede do valor já creditado;
Caso o cancelamento seja feito em menos de ${dias} dias da data prevista de entrada, pagará multa correspondente a 01 (uma) diária, o saldo restante se houver será transferido como crédito a ser usado em outra data;
Política diferenciada para Réveillon, Carnaval e feriados prolongados.`;
    
    const valorTotalComExtras = (reservaLocal.valorTotal || 0) + despesasExtras.reduce((acc, item) => acc + item.valor, 0);
    const totalPago = (reservaLocal.adiantamento?.valor || 0) + (reservaLocal.pagamentoRestante?.valor || 0);
    const desconto = reservaLocal.desconto || 0;
    const saldoDevedor = valorTotalComExtras - desconto - totalPago;

    const corpoEmail = `Prezado(a) ${reservaLocal.hospedeNome},

Agradecemos por escolher a AABB para sua hospedagem!

Seguem os detalhes da sua reserva:
--------------------------------------------------
VOUCHER: ${reservaLocal.codigoVoucher}
--------------------------------------------------

Chalé: ${reservaLocal.chaleNome}
Check-in: ${formatarData(reservaLocal.dataCheckin)}
Check-out: ${formatarData(reservaLocal.dataCheckout)}

Hóspedes Registrados:
- ${reservaLocal.hospedeNome} (Titular)
${acompanhantesTexto}

Valor Total da Estadia: ${formatarMoeda(valorTotalComExtras - desconto)}
Total Pago: ${formatarMoeda(totalPago)}
Saldo Devedor: ${formatarMoeda(saldoDevedor)}

Atenciosamente,
Equipe AABB

---
OBS: Não é permitido a entrada de animais.

${politicaCancelamento}`;

    const link = `mailto:${detalhesHospede.email}?subject=Confirmação de Reserva - AABB&body=${encodeURIComponent(corpoEmail)}`;
    window.location.href = link;
  };

  const handleGerarPDF = async () => {
    setLoadingPdf(true);
    const canvas = await html2canvas(resumoRef.current, { scale: 2 });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
    pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
    pdf.save(`reserva-${detalhesHospede?.nome}.pdf`);
    setLoadingPdf(false);
  };

  if (loading || !reservaLocal || !detalhesHospede) {
    return (
        <Dialog open={true} onClose={onClose}>
            <DialogContent sx={{p:4, display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                <CircularProgress />
            </DialogContent>
        </Dialog>
    );
  }

  const valorTotalBruto = reservaLocal.valorTotal || 0;
  const totalDespesasExtras = despesasExtras.reduce((acc, item) => acc + item.valor, 0);
  const valorTotalComExtras = valorTotalBruto + totalDespesasExtras;
  const desconto = reservaLocal.desconto || 0;
  const valorAdiantamento1 = reservaLocal.adiantamento?.valor || 0;
  const valorAdiantamento2 = reservaLocal.adiantamento2?.valor || 0;
  const valorAdiantamentoTotal = valorAdiantamento1 + valorAdiantamento2;
  const valorPagamentoFinal = reservaLocal.pagamentoRestante?.valor || 0;
  const saldoDevedor = valorTotalComExtras - desconto - valorAdiantamentoTotal - valorPagamentoFinal;
  
  // --- VARIÁVEL QUE FALTAVA, AGORA DEFINIDA AQUI ---
  const reservaParaPagamentoEVoucher = { ...reservaLocal, valorTotalComExtras, totalDespesasExtras };
  
  const hoje = getHojeFormatado();
  const agora = new Date();
  const horarioCheckinConfig = configuracoes?.checkin || '14:00';
  const [horaCheckin, minutoCheckin] = horarioCheckinConfig.split(':').map(Number);

  const podeFazerCheckin = 
    (reservaLocal.statusReserva === 'Confirmada' || reservaLocal.statusReserva === 'Pago Total') && 
    reservaLocal.dataCheckin === hoje && 
    (agora.getHours() > horaCheckin || (agora.getHours() === horaCheckin && agora.getMinutes() >= minutoCheckin));

  const podeFazerCheckout = reservaLocal.statusReserva === 'Hospedado' && saldoDevedor <= 0;
  const reservaAtiva = !['Finalizada', 'Cancelada'].includes(reservaLocal.statusReserva);
  const podeMudarDeChale = ['Confirmada', 'Pago Total', 'Hospedado'].includes(reservaLocal.statusReserva);

  const InfoItem = ({ label, value, bold = false }) => ( <Typography variant="body2" sx={{ mb: 1 }}><strong>{label}:</strong><Box component="span" sx={{ fontWeight: bold ? 'bold' : 'normal', ml: 0.5 }}>{value || 'Não informado'}</Box></Typography> );

  const StatusReserva = () => {
    if (reservaLocal.statusReserva === 'Confirmada' && valorAdiantamentoTotal === 0) {
      return 'Pendente Pagto Inicial';
    }
    if (reservaLocal.statusReserva === 'Finalizada') {
        return 'Check-out Realizado';
    }
    return reservaLocal.statusReserva;
  };
  
  return (
    <>
      <Dialog open={true} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Reserva de: {detalhesHospede.nome}
          <IconButton onClick={onClose}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers ref={resumoRef}>
            <Grid container spacing={2}>
               <Grid item xs={12} md={6} lg={4}>
                <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
                  <Typography variant="h6" gutterBottom>Resumo da Reserva</Typography>
                  <Divider sx={{ mb: 2 }} />
                  <InfoItem label="Voucher" value={reservaLocal.codigoVoucher} bold />
                  <InfoItem label="Status" value={<StatusReserva />} bold />
                  <InfoItem label="Check-in" value={formatarData(reservaLocal.dataCheckin)} />
                  <InfoItem label="Check-out" value={formatarData(reservaLocal.dataCheckout)} />
                  <InfoItem label="Dias" value={`${reservaLocal.numDiarias} dia(s)`} />
                  <InfoItem label="Acomodação" value={reservaLocal.chaleNome} />
                  <InfoItem label="Hóspedes" value={reservaLocal.numeroHospedes} />
                </Paper>
              </Grid>

              <Grid item xs={12} md={6} lg={4}>
                <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
                  <Typography variant="h6" gutterBottom>Detalhes do Hóspede</Typography>
                  <Divider sx={{ mb: 2 }} />
                  <InfoItem label="CPF" value={detalhesHospede.cpf} />
                  <InfoItem label="Telefone" value={detalhesHospede.celular} />
                  <InfoItem label="E-mail" value={detalhesHospede.email} />
                  <InfoItem label="Cidade/UF" value={`${detalhesHospede.cidade || ''} / ${detalhesHospede.estado || ''}`} />
                </Paper>
              </Grid>

              <Grid item xs={12} md={12} lg={4}>
                <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
                  <Typography variant="h6" gutterBottom>Situação Financeira</Typography>
                  <Divider sx={{ mb: 2 }} />
                  <InfoItem label="Valor Diária" value={formatarMoeda(reservaLocal.valorDiaria)} />
                  <InfoItem label="Valor da Estadia" value={formatarMoeda(valorTotalBruto)} />
                  <InfoItem label="Consumo Extra" value={formatarMoeda(totalDespesasExtras)} />
                  <InfoItem label="Desconto" value={`${formatarMoeda(desconto)} ${reservaLocal.motivoDesconto ? `(${reservaLocal.motivoDesconto})` : ''}`} />
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>Total Geral: {formatarMoeda(valorTotalComExtras - desconto)}</Typography>
                  <Divider sx={{ my: 2 }} />
                  <InfoItem label="1º Adiantamento" value={`${formatarMoeda(valorAdiantamento1)} (${reservaLocal.adiantamento?.metodo || 'N/A'}) ${reservaLocal.adiantamento?.data ? 'em ' + formatarData(reservaLocal.adiantamento.data) : ''}`} />
                  {valorAdiantamento2 > 0 && 
                    <InfoItem label="2º Adiantamento" value={`${formatarMoeda(valorAdiantamento2)} (${reservaLocal.adiantamento2?.metodo || 'N/A'}) ${reservaLocal.adiantamento2?.data ? 'em ' + formatarData(reservaLocal.adiantamento2.data) : ''}`} />
                  }
                  <InfoItem label="Pagamento Final" value={`${formatarMoeda(valorPagamentoFinal)} (${reservaLocal.pagamentoRestante?.metodo || 'N/A'}) ${reservaLocal.pagamentoRestante?.data ? 'em ' + formatarData(reservaLocal.pagamentoRestante.data) : ''}`} />
                  <Typography variant="h6" color={saldoDevedor > 0 ? 'error.main' : 'success.main'} sx={{ mt: 1 }}>Saldo Devedor: {formatarMoeda(saldoDevedor)}</Typography>
                </Paper>
              </Grid>
              
              <Grid item xs={12}>
                <TableContainer component={Paper} elevation={2}>
                  <Typography variant="h6" sx={{ p: 2 }}>Consumo / Despesas Extras</Typography>
                  <Table size="small">
                      <TableHead>
                        <TableRow><TableCell>Descrição</TableCell><TableCell align="right">Valor</TableCell><TableCell align="center">Ação</TableCell></TableRow>
                      </TableHead>
                      <TableBody>
                        {despesasExtras.length > 0 ? ( despesasExtras.map((d) => ( <TableRow key={d.id}><TableCell>{d.descricao}</TableCell><TableCell align="right">{formatarMoeda(d.valor)}</TableCell><TableCell align="center"><IconButton size="small" color="error" onClick={()=>handleExcluirDespesa(d.id)}><DeleteIcon fontSize='inherit'/></IconButton></TableCell></TableRow> )) ) : ( <TableRow><TableCell colSpan={3} align="center">Nenhuma despesa extra</TableCell></TableRow> )}
                      </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
        </DialogContent>
        <DialogActions sx={{ p: '16px 24px', justifyContent: 'space-between', flexWrap: 'wrap' }}>
            <Box>
                {reservaLocal.statusReserva === 'Cancelada' ? (
                     <Tooltip title="Excluir Registro Permanente">
                        <Button variant="outlined" color="error" startIcon={<DeleteForeverIcon />} onClick={handleExcluirReserva}> Excluir Registro </Button>
                    </Tooltip>
                ) : (
                    <>
                        {reservaAtiva && <Tooltip title="Editar Reserva"><IconButton color="primary" onClick={() => onEditar(reservaLocal)}><EditIcon /></IconButton></Tooltip>}
                        {valorAdiantamentoTotal === 0 && reservaAtiva && <Tooltip title="Excluir Reserva"><IconButton color="error" onClick={handleExcluirReserva}><DeleteForeverIcon /></IconButton></Tooltip>}
                        {podeMudarDeChale && <Tooltip title="Mudar de Chalé"><IconButton color="info" onClick={() => setIsMudarChaleModalOpen(true)}><SwapHorizIcon /></IconButton></Tooltip>}
                        <Tooltip title="Enviar Confirmação por E-mail"><IconButton color="secondary" onClick={handleEnviarEmail}><EmailIcon /></IconButton></Tooltip>
                        <Tooltip title="Gerar PDF"><IconButton color="warning" onClick={handleGerarPDF} disabled={loadingPdf}>{loadingPdf ? <CircularProgress size={24}/> : <PictureAsPdfIcon />}</IconButton></Tooltip>
                    </>
                )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {reservaAtiva && (
                    <>
                        <Button variant="outlined" startIcon={<AddShoppingCartIcon/>} onClick={() => setIsDespesaModalOpen(true)}>Despesa</Button>
                        <Button variant="outlined" startIcon={<PaymentIcon/>} onClick={() => setIsPagamentoModalOpen(true)}>Pagamento</Button>
                    </>
                )}
                <Button variant="outlined" startIcon={<ReceiptIcon/>} onClick={() => setIsFichaModalOpen(true)}>Ficha</Button>
                <Button variant="outlined" startIcon={<LocalActivityIcon/>} onClick={() => setIsVouchersModalOpen(true)} disabled={loading}>Vouchers</Button>
                {valorAdiantamentoTotal > 0 && reservaAtiva && (
                    <Button variant="outlined" color="error" startIcon={<DoNotDisturbOnIcon/>} onClick={() => setIsCancelamentoModalOpen(true)}>Cancelar Reserva</Button>
                )}
                {podeFazerCheckout && ( <Button variant="contained" color="info" startIcon={<CheckCircleIcon />} onClick={handleCheckout}>Realizar Check-out</Button> )}
                {podeFazerCheckin && ( <Button variant="contained" color="success" startIcon={isCheckingIn ? <CircularProgress size={20} color="inherit" /> : <CheckCircleOutlineIcon />} onClick={handleCheckIn} disabled={isCheckingIn}>{isCheckingIn ? 'Processando...' : 'Realizar Check-in'}</Button> )}
            </Box>
        </DialogActions>
      </Dialog>
      
      {isPagamentoModalOpen && ( <PagamentoModal open={isPagamentoModalOpen} reserva={reservaParaPagamentoEVoucher} onClose={() => setIsPagamentoModalOpen(false)} onSave={handleSavePagamento} /> )}
      {isDespesaModalOpen && ( <DespesaExtraModal open={isDespesaModalOpen} onClose={() => setIsDespesaModalOpen(false)} onSave={handleSaveDespesa} /> )}
      {isFichaModalOpen && ( <FichaCheckinModal open={isFichaModalOpen} onClose={() => setIsFichaModalOpen(false)} reserva={reservaLocal} hospede={detalhesHospede} despesasExtras={despesasExtras} /> )}
      {isVouchersModalOpen && ( <VouchersModal open={isVouchersModalOpen} onClose={() => setIsVouchersModalOpen(false)} reserva={reservaParaPagamentoEVoucher} hospede={detalhesHospede} /> )}
      {isCancelamentoModalOpen && ( <CancelamentoModal open={isCancelamentoModalOpen} reserva={reservaLocal} onClose={() => setIsCancelamentoModalOpen(false)} onConfirm={handleConfirmarCancelamento} /> )}
      {isMudarChaleModalOpen && ( <MudarChaleModal open={isMudarChaleModalOpen} reserva={reservaLocal} chales={chales} onClose={() => setIsMudarChaleModalOpen(false)} onConfirm={handleConfirmarMudanca} /> )}
    </>
  );
};

export default DetalhesReservaModal;

